/* global customElements, HTMLElement */
(function () {
  'use strict';

  // ============================================================
  // Compat shim for Node-based unit tests. In the browser these globals
  // exist and this block is skipped. In Node, evaluating
  // `class X extends HTMLElement` would otherwise throw before the export
  // branch at the bottom can return.
  // ============================================================
  if (typeof HTMLElement === 'undefined') {
    globalThis.HTMLElement = class {};
    globalThis.customElements = { define: function () {} };
  }

  // ============================================================
  // Constants
  // ============================================================

  // v3 cache key (schema carries `source`; v2 predates it).
  const CACHE_KEY = 'deuxpardeux:estShip:v3';
  const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  const WORKER_TIMEOUT_MS = 6000;
  const GEO_TIMEOUT_MS = 10000;

  // Ship-from warehouse clock. Cut-offs / "today-tomorrow" / the countdown are
  // evaluated in THIS timezone, not the visitor's. Hardcoded (single Montreal
  // warehouse); change here if the fulfilment origin ever moves.
  const SHIP_FROM_TZ = 'America/Toronto';
  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // EU-27 + EEA shoppers handled by the eurozone block. Add codes as launched.
  const EUROZONE_CODES = new Set([
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT',
    'LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
  ]);
  // Canadian provinces grouped as "Quebec & Ontario" for the non-FSA-matched
  // standard ground service tier.
  const CA_QC_ON = new Set(['QC','ON']);

  // First letter of a Canadian FSA encodes the province/region. We only need
  // the QC vs ON split for zone grouping, but the full map is cheap and lets
  // us derive a region for manually-typed postals with no API round-trip.
  //   A NL · B NS · C PE · E NB · G/H/J QC · K/L/M/N/P ON · R MB · S SK
  //   T AB · V BC · X NT/NU · Y YT
  const CA_FSA_LETTER_TO_REGION = {
    A: 'NL', B: 'NS', C: 'PE', E: 'NB',
    G: 'QC', H: 'QC', J: 'QC',
    K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON',
    R: 'MB', S: 'SK', T: 'AB', V: 'BC', X: 'NT', Y: 'YT',
  };

  const DEBUG_SCENARIOS = {
    sd_nd_all_week:   { code: 'DEBUG-7D', sd: 1, nd: 1, days: 'SMTWTFS' },
    sd_nd_weekdays:   { code: 'DEBUG-WD', sd: 1, nd: 1, days: '.MTWTF.' },
    nd_only_all_week: { code: 'DEBUG-N7', sd: 0, nd: 1, days: 'SMTWTFS' },
    nd_only_weekdays: { code: 'DEBUG-NW', sd: 0, nd: 1, days: '.MTWTF.' },
    today_not_marked: { code: 'DEBUG-NA', sd: 1, nd: 1, days: '.......' },
  };

  // ============================================================
  // Pure helpers
  // ============================================================

  function normalizePostal(z) {
    if (z == null) return null;
    const s = String(z).toUpperCase().replace(/\s+/g, '');
    return s === '' ? null : s;
  }

  function parseCutoff(s) {
    if (typeof s !== 'string') return 1439;
    const m = s.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return 1439;
    const h = Number(m[1]);
    const mm = Number(m[2]);
    if (h < 0 || h > 23 || mm < 0 || mm > 59) return 1439;
    return h * 60 + mm;
  }

  function nowMinutes(date) {
    const d = date || new Date();
    return d.getHours() * 60 + d.getMinutes();
  }

  function findRow(map, postal) {
    if (!postal || !map) return null;
    const direct = map.get(postal);
    if (direct) return direct;
    if (postal.length >= 3) {
      const fsa = map.get(postal.slice(0, 3));
      if (fsa) return fsa;
    }
    return null;
  }

  // Guess the country from a postal's shape. Used for manually-typed input so
  // we don't force the visitor to pick a country. Falls back to null (caller
  // then uses the active market country).
  function inferCountryFromPostal(postal) {
    const s = normalizePostal(postal);
    if (!s) return null;
    if (/^[ABCEGHJKLMNPRSTVXY]\d[A-Z]/.test(s)) return 'CA';
    if (/^\d{5}(-?\d{4})?$/.test(s)) return 'US';
    if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(s)) return 'GB';
    return null;
  }

  // Derive a Canadian province code from an FSA first letter (QC/ON grouping
  // is all the zone logic needs, but we return the full province when known).
  function caRegionFromPostal(postal) {
    const s = normalizePostal(postal);
    if (!s) return null;
    return CA_FSA_LETTER_TO_REGION[s.charAt(0)] || null;
  }

  // Lenient, country-aware format check for the manual-entry field. We accept
  // an FSA (first 3) for Canada because that is all the delivery logic needs.
  function validatePostalFormat(postal, country) {
    const s = normalizePostal(postal);
    if (!s) return false;
    const c = (country || '').toUpperCase();
    if (c === 'CA') return /^[ABCEGHJKLMNPRSTVXY]\d[A-Z](\d[A-Z]\d)?$/.test(s);
    if (c === 'US') return /^\d{5}(-?\d{4})?$/.test(s);
    if (c === 'GB') return /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(s);
    // Unknown market: accept anything that looks postal-ish.
    return s.length >= 3;
  }

  function resolveZone(country, region, row) {
    if (country === 'CA') {
      if (row && row.sd === 1) return 'sd';
      if (row && row.nd === 1) return 'nd';
      const r = (region || '').toUpperCase();
      if (CA_QC_ON.has(r)) return 'ca_qc_on';
      return 'ca_other';
    }
    if (country === 'US') return 'us';
    if (country === 'GB') return 'uk';
    if (EUROZONE_CODES.has(country)) return 'eu';
    return 'intl';
  }

  function nextCutoffDate(now, cutoffMin, allowToday) {
    const d = new Date(now.getTime());
    d.setSeconds(0, 0);
    const h = Math.floor(cutoffMin / 60);
    const m = cutoffMin % 60;
    d.setHours(h, m, 0, 0);
    if (!allowToday) d.setDate(d.getDate() + 1);
    return d;
  }

  // ---- Timezone-aware time (cut-offs are the SHIP-FROM warehouse's wall clock,
  // e.g. Montreal / America/Toronto — NOT the visitor's local time). ----

  const WEEKDAY_IDX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  // The visitor's own local wall clock (fallback when no timezone configured).
  function localWallClock(now) {
    const d = now || new Date();
    return {
      year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
      h: d.getHours(), m: d.getMinutes(), weekdayIdx: d.getDay(),
    };
  }

  // `now` as it reads on the clock in `tz`.
  function wallClockInTZ(now, tz) {
    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', weekday: 'short',
      });
      const p = {};
      dtf.formatToParts(now).forEach(function (x) { p[x.type] = x.value; });
      return {
        year: +p.year, month: +p.month, day: +p.day,
        h: parseInt(p.hour, 10) % 24, m: +p.minute,
        weekdayIdx: WEEKDAY_IDX[p.weekday],
      };
    } catch (_e) {
      return localWallClock(now);
    }
  }

  // Offset (ms) such that: (wall-clock-in-tz interpreted as UTC) - offset = real epoch.
  function tzOffsetMs(date, tz) {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = {};
    dtf.formatToParts(date).forEach(function (x) { p[x.type] = x.value; });
    const asIfUTC = Date.UTC(+p.year, +p.month - 1, +p.day, parseInt(p.hour, 10) % 24, +p.minute, +p.second);
    return asIfUTC - date.getTime();
  }

  // Real Date for "cutoffMin on (wc's date + addDays)" in `tz`.
  function cutoffDateInTZ(wc, cutoffMin, addDays, tz) {
    const h = Math.floor(cutoffMin / 60);
    const mi = cutoffMin % 60;
    const asUTC = Date.UTC(wc.year, wc.month - 1, wc.day + addDays, h, mi, 0);
    try {
      const off = tzOffsetMs(new Date(asUTC), tz);
      return new Date(asUTC - off);
    } catch (_e) {
      return new Date(asUTC);
    }
  }

  /**
   * Convert a snake_case zone id to the camelCase config key suffix used by
   * the `cutoff*` / `template*` config fields ('sd' -> 'Sd', 'ca_qc_on' ->
   * 'CaQcOn').
   */
  function toAttrCase(zone) {
    return String(zone || '')
      .split('_')
      .map(function (s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; })
      .join('');
  }

  function pad2(n) {
    return String(Math.max(0, n) | 0).padStart(2, '0');
  }

  function formatDuration(ms) {
    let secs = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(secs / 3600); secs -= h * 3600;
    const m = Math.floor(secs / 60); secs -= m * 60;
    return { h: h, m: m, s: secs };
  }

  // "02h:23m:21s", or "23m:21s" under an hour.
  function formatTicker(h, m, s) {
    if (h > 0) return pad2(h) + 'h:' + pad2(m) + 'm:' + pad2(s) + 's';
    return pad2(m) + 'm:' + pad2(s) + 's';
  }

  // "Only show Obibox times": a live same-day / next-day countdown, or the
  // weekend Monday (Obibox next-day areas) message.
  function isObiboxTime(res, nowMs) {
    if (!res) return false;
    if (res.effectiveZone === 'monday') return true;
    return !!(res.targetDate && res.targetDate.getTime() > nowMs);
  }

  function formatPlain(h, m) {
    if (h <= 0 && m <= 0) return '';
    if (h <= 0) return m + ' min left';
    if (m <= 0) return h + ' h left';
    return h + ' h ' + m + ' min left';
  }

  /**
   * Resolve the zone + cut-off + template + countdown target for a location.
   * Mirrors the original PDP logic. Returns `{ template: '' }` when nothing
   * should be shown (e.g. SD/ND zone whose FSA row isn't served today with no
   * next-day fall-through), so callers hide the block.
   */
  function computeResolution(loc, config, now, opts) {
    const country = loc.country;
    const region = loc.region;
    const row = loc.row || null;
    const tz = config.timezone || null;
    // Same-day / next-day are Canada-specific OVERRIDES of the standard message.
    // The cart can suppress either (via the cart toggles) — suppressing both just
    // falls through to the standard regional message. Suppressing same-day keeps
    // the free-shipping message honest (same-day isn't free).
    const suppressSameDay = !!(opts && opts.suppressSameDay);
    const suppressNextDay = !!(opts && opts.suppressNextDay);

    // All time reasoning happens on the ship-from clock (tz), not the visitor's.
    const wc = tz ? wallClockInTZ(now, tz) : localWallClock(now);
    const nowMin = wc.h * 60 + wc.m;
    const todayIdx = wc.weekdayIdx;

    // Build a cut-off Date in tz (or the visitor-local fallback).
    const cutoffDate = function (min, allowToday) {
      return tz ? cutoffDateInTZ(wc, min, allowToday ? 0 : 1, tz) : nextCutoffDate(now, min, allowToday);
    };

    const baseZone = resolveZone(country, region, row);
    const todayWord = config.todayWord || 'today';
    const tomorrowWord = config.tomorrowWord || 'tomorrow';

    let effectiveZone = baseZone;
    let dayWord = todayWord;
    let targetDate = null;
    let cutoffStr = '';

    // Only Same-day / Next-day zones have cut-offs + a live countdown. The rule:
    //   • order before TODAY's SD cut-off  → arrives TODAY   (same-day)
    //   • order before TODAY's ND cut-off  → arrives TOMORROW (next-day)
    // The countdown always targets a cut-off you can still make TODAY. Once both
    // are missed for today (or the day isn't served), same/next-day can't be
    // promised for a today-order, so we fall back to the standard regional
    // message (static, no countdown) — except over the weekend, when next-day
    // areas get the Monday Obibox message instead.
    if (baseZone === 'sd' || baseZone === 'nd') {
      const servedToday = !(row && typeof row.days === 'string' && row.days.charAt(todayIdx) === '.');
      const sdCutStr = config.cutoffSd || '13:00';
      const ndCutStr = config.cutoffNd || '13:00';
      const sdCut = parseCutoff(sdCutStr);
      const ndCut = parseCutoff(ndCutStr);

      if (!suppressSameDay && row && row.sd === 1 && servedToday && nowMin < sdCut) {
        effectiveZone = 'sd'; dayWord = todayWord; targetDate = cutoffDate(sdCut, true); cutoffStr = sdCutStr;
      } else if (!suppressNextDay && row && row.nd === 1 && servedToday && nowMin < ndCut) {
        effectiveZone = 'nd'; dayWord = tomorrowWord; targetDate = cutoffDate(ndCut, true); cutoffStr = ndCutStr;
      } else if (!suppressNextDay && row && row.nd === 1 &&
                 ((todayIdx === 5 && nowMin >= ndCut) || todayIdx === 6 || todayIdx === 0)) {
        // Weekend (Friday after the ND cut-off through Sunday): Obibox
        // delivers next-day areas on Monday. Static message, no countdown.
        effectiveZone = 'monday'; dayWord = config.mondayWord || 'Monday'; targetDate = null; cutoffStr = '';
      } else {
        effectiveZone = CA_QC_ON.has((region || '').toUpperCase()) ? 'ca_qc_on' : 'ca_other';
        dayWord = tomorrowWord; targetDate = null; cutoffStr = '';
      }
    }
    // else: standard zone — static message, no cut-off, no countdown.

    const template = config['template' + toAttrCase(effectiveZone)] || '';

    return {
      zone: baseZone,
      effectiveZone: effectiveZone,
      template: template,
      dayWord: dayWord,
      targetDate: targetDate,
      todayIdx: todayIdx,
      nowLabel: pad2(wc.h) + ':' + pad2(wc.m),
      cutoffStr: cutoffStr || '—',
      timezone: tz,
    };
  }

  // ============================================================
  // Cache (localStorage, same-day TTL). Persists the confirmed/estimated
  // location so PDP ↔ cart share it and reloads don't re-detect.
  // ============================================================

  function readCache(now) {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (!v || typeof v !== 'object') return null;
      const fetchedAt = new Date(v.fetched_at_iso).getTime();
      if (!isFinite(fetchedAt)) return null;
      if (now.getTime() - fetchedAt >= CACHE_TTL_MS) return null;
      if (new Date(fetchedAt).toDateString() !== now.toDateString()) return null;
      return {
        postal: v.postal != null ? v.postal : null,
        country: v.country != null ? v.country : null,
        region: v.region != null ? v.region : null,
        city: v.city != null ? v.city : null,
        source: v.source || 'ip',
      };
    } catch (_e) {
      return null;
    }
  }

  function writeCache(loc, now) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        postal: loc.postal != null ? loc.postal : null,
        country: loc.country != null ? loc.country : null,
        region: loc.region != null ? loc.region : null,
        city: loc.city != null ? loc.city : null,
        source: loc.source || 'ip',
        fetched_at_iso: now.toISOString(),
      }));
    } catch (_e) { /* private mode / storage full — skip */ }
  }

  function clearCache() {
    try { localStorage.removeItem(CACHE_KEY); } catch (_e) { /* ignore */ }
  }

  // ============================================================
  // Network
  // ============================================================

  // IP-based preliminary guess. The rewritten worker returns Cloudflare's
  // native edge geolocation (`request.cf.postalCode` etc.) — see
  // workers/estimated-shipping-worker.js.
  async function fetchWorkerIp(url) {
    if (!url) return null;
    const ctrl = new AbortController();
    const timer = setTimeout(function () { ctrl.abort(); }, WORKER_TIMEOUT_MS);
    try {
      const r = await fetch(url, { method: 'GET', mode: 'cors', signal: ctrl.signal, credentials: 'omit' });
      if (!r.ok) return null;
      const data = await r.json();
      return normalizeGeoResponse(data);
    } catch (_e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  // Reverse-geocode precise coordinates (from the browser Geolocation API)
  // through the SAME worker, which calls Google Geocoding server-side so the
  // API key never reaches the client.
  async function reverseGeocode(workerUrl, lat, lng) {
    if (!workerUrl) return null;
    const ctrl = new AbortController();
    const timer = setTimeout(function () { ctrl.abort(); }, WORKER_TIMEOUT_MS);
    try {
      const u = new URL('reverse', workerUrl);
      u.searchParams.set('lat', String(lat));
      u.searchParams.set('lng', String(lng));
      const r = await fetch(u.toString(), { method: 'GET', mode: 'cors', signal: ctrl.signal, credentials: 'omit' });
      if (!r.ok) return null;
      const data = await r.json();
      return normalizeGeoResponse(data);
    } catch (_e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  function normalizeGeoResponse(data) {
    if (!data || typeof data !== 'object') return null;
    return {
      postal: data.postal_code ? String(data.postal_code) : null,
      country: data.country ? String(data.country).toUpperCase() : null,
      region: data.region ? String(data.region).toUpperCase() : null,
      city: data.city ? String(data.city) : null,
    };
  }

  async function loadZonesMap(url) {
    if (window.__fsaZonesMap) return window.__fsaZonesMap;
    if (!url) return null;
    try {
      const r = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!r.ok) return null;
      const arr = await r.json();
      if (!Array.isArray(arr)) return null;
      const map = new Map();
      for (let i = 0; i < arr.length; i++) {
        const row = arr[i];
        if (row && typeof row.code === 'string') map.set(row.code.toUpperCase(), row);
      }
      window.__fsaZonesMap = map;
      return map;
    } catch (_e) {
      return null;
    }
  }

  // Browser geolocation → Promise<{lat,lng}>. Rejects with a reason string
  // ('denied' | 'unavailable' | 'unsupported') the dialog turns into copy.
  function getCurrentPosition() {
    return new Promise(function (resolve, reject) {
      if (!('geolocation' in navigator)) { reject('unsupported'); return; }
      navigator.geolocation.getCurrentPosition(
        function (pos) { resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        function (err) { reject(err && err.code === 1 ? 'denied' : 'unavailable'); },
        { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 0 }
      );
    });
  }

  // ============================================================
  // Store — single source of truth for the resolved location. All
  // <estimated-shipping> instances + the dialog read/write through here and
  // re-render on the 'change' event.
  // ============================================================

  const Store = {
    config: null,
    now: null,
    debug: false,
    log: function () {},
    state: null,          // { postal, country, region, city, source, row, resolution }
    _listeners: [],
    _initialized: false,
    _zonesMap: null,

    subscribe: function (fn) {
      this._listeners.push(fn);
      return function () {
        const i = this._listeners.indexOf(fn);
        if (i >= 0) this._listeners.splice(i, 1);
      }.bind(this);
    },

    _emit: function () {
      for (let i = 0; i < this._listeners.length; i++) {
        try { this._listeners[i](this.state); } catch (_e) { /* isolate listeners */ }
      }
      try {
        window.dispatchEvent(new CustomEvent('estship:change', { detail: this.state }));
      } catch (_e) { /* old browsers */ }
    },

    // Initialise once from the first element's config. Kicks off the
    // account → cache → IP resolution pipeline.
    init: async function (config, opts) {
      if (this._initialized) return;
      this._initialized = true;
      this.config = config;
      this.debug = !!(opts && opts.debug);
      this.log = this.debug
        ? function (label, data) { console.log('[estimated-shipping] ' + label, data === undefined ? '' : data); }
        : function () {};
      this.now = (opts && opts.now) || new Date();

      this.log('init config', config);

      // The FSA zones map is loaded lazily: _set() fetches it when resolving a
      // Canadian postal, and the dialog fetches it for manual-entry
      // suggestions. We don't block initial resolution on it.
      await this._resolveInitial(opts || {});
    },

    _resolveInitial: async function (opts) {
      const config = this.config;
      const marketCA = (config.marketCountry || '').toUpperCase() === 'CA';

      // QA overrides (theme editor / preview) work regardless of market so we
      // can force any state.
      if (opts.debugScenario && DEBUG_SCENARIOS[opts.debugScenario]) {
        const row = DEBUG_SCENARIOS[opts.debugScenario];
        this.log('using debug scenario', opts.debugScenario);
        await this._set({
          postal: row.code, country: 'CA', region: 'QC', city: '(debug)',
          source: 'debug', row: row,
        }, false);
        return;
      }
      if (opts.testPostal) {
        this.log('using test_postal override', opts.testPostal);
        await this._set({
          postal: opts.testPostal,
          country: opts.testCountry || config.marketCountry || 'CA',
          region: opts.testRegion || null,
          city: null, source: 'test',
        }, false);
        return;
      }

      // Non-Canada destination (per the Shopify country selector): the postal
      // never matters — show the static country-level message only. No IP call,
      // no dialog.
      if (!marketCA) {
        this.log('non-CA market, static message', config.marketCountry);
        await this._set({
          postal: null, country: config.marketCountry || null, region: null, city: null,
          source: 'market',
        }, false);
        return;
      }

      // --- Canada: postal pipeline (account → cache → IP → prompt). ---

      // 1. Logged-in customer's default address (if it's Canadian) — no fetch.
      if (config.accountPostal && (config.accountCountry || 'CA').toUpperCase() === 'CA') {
        this.log('using account address', config.accountPostal);
        await this._set({
          postal: config.accountPostal, country: 'CA',
          region: config.accountRegion || null,
          city: null, source: 'account',
        }, false);
        return;
      }

      // 2. Persisted Canadian choice from a previous view.
      const cached = readCache(this.now);
      if (cached && cached.postal && (cached.country || 'CA').toUpperCase() === 'CA') {
        this.log('using cache', cached);
        await this._set(cached, false);
        return;
      }

      // 3. IP guess — accepted ONLY when Cloudflare also says Canada. A
      //    Canadian on a US-looking IP (border / ISP routing) falls through to
      //    the prompt instead of getting a bogus foreign postal.
      if (config.enableIp !== false) {
        const ip = await fetchWorkerIp(config.workerUrl);
        this.log('worker IP response', ip);
        if (ip && ip.postal && (ip.country || '').toUpperCase() === 'CA') {
          await this._set({
            postal: ip.postal, country: 'CA', region: ip.region, city: ip.city,
            source: 'ip',
          }, true);
          return;
        }
      }

      // 4. Canada selected but no postal yet → empty state → "enter postal" prompt.
      await this._set({
        postal: null, country: 'CA', region: null, city: null,
        source: null,
      }, false);
    },

    // Commit a location: enrich with FSA row + resolution, persist, emit.
    _set: async function (loc, persist) {
      const config = this.config;
      let country = loc.country ? String(loc.country).toUpperCase() : null;
      let region = loc.region ? String(loc.region).toUpperCase() : null;
      const postal = normalizePostal(loc.postal);

      // Canadian region backfill from the FSA first letter when unknown.
      if (country === 'CA' && !region && postal) {
        region = caRegionFromPostal(postal);
      }

      let row = loc.row || null;
      if (!row && country === 'CA' && postal) {
        if (!this._zonesMap) this._zonesMap = await loadZonesMap(config.zonesUrl);
        row = findRow(this._zonesMap, postal);
      }

      const enriched = {
        postal: postal,
        country: country,
        region: region,
        city: loc.city || null,
        source: loc.source || null,
        row: row,
      };
      // Resolve when we have a postal (Canada) OR a non-CA country (static
      // country-level message needs no postal).
      const canResolve = !!postal || (!!country && country !== 'CA');
      enriched.resolution = canResolve ? computeResolution(enriched, config, this.now) : null;

      this.state = enriched;
      this.log('state set', enriched);
      if (persist) writeCache(enriched, this.now);
      this._emit();
    },

    // Public: commit a visitor-supplied location (manual entry / geolocation).
    setLocation: function (loc) {
      return this._set({
        postal: loc.postal, country: loc.country, region: loc.region, city: loc.city || null,
        source: loc.source || 'manual',
      }, true);
    },
  };

  // ============================================================
  // Notification rendering (token replacement, no innerHTML)
  //
  // Tokens: [day] today/tomorrow · [postal] full postal · [timer] live
  // "02h:23m:21s" countdown · [time-left] plain-text "X h Y min left".
  // Returns a setInterval handle (or null) the caller must clear.
  // `opts.onExpire` (optional) runs once when the countdown reaches zero.
  // ============================================================

  function renderNotification(el, opts) {
    el.replaceChildren();
    const tpl = String(opts.template || '');
    const postalText = opts.postal || '';
    const targetDate = opts.targetDate;

    let timerSpan = null;
    let timeLeftSpan = null;

    const re = /\[(day|postal|timer|time-left)\]/g;
    let lastIdx = 0;
    let match;
    while ((match = re.exec(tpl)) !== null) {
      const textBefore = tpl.slice(lastIdx, match.index);
      if (textBefore) el.appendChild(document.createTextNode(textBefore));
      const token = match[1];
      if (token === 'day') {
        const strong = document.createElement('strong');
        strong.textContent = opts.dayWord;
        el.appendChild(strong);
      } else if (token === 'postal') {
        if (postalText) {
          const strong = document.createElement('strong');
          strong.textContent = postalText;
          el.appendChild(strong);
        }
      } else if (token === 'timer') {
        if (targetDate) {
          timerSpan = document.createElement('span');
          timerSpan.className = 'estimated-shipping__timer';
          el.appendChild(timerSpan);
        }
      } else if (token === 'time-left') {
        if (targetDate) {
          timeLeftSpan = document.createElement('span');
          timeLeftSpan.className = 'estimated-shipping__time-left';
          el.appendChild(timeLeftSpan);
        }
      }
      lastIdx = re.lastIndex;
    }
    const tail = tpl.slice(lastIdx);
    if (tail) el.appendChild(document.createTextNode(tail));

    if (timerSpan || timeLeftSpan) {
      let handle = null;
      const tick = function () {
        const ms = targetDate.getTime() - Date.now();
        if (ms <= 0) {
          if (timerSpan) timerSpan.textContent = formatTicker(0, 0, 0);
          if (timeLeftSpan) timeLeftSpan.textContent = '';
          if (handle) clearInterval(handle);
          if (typeof opts.onExpire === 'function') opts.onExpire();
          return;
        }
        const d = formatDuration(ms);
        if (timerSpan) timerSpan.textContent = formatTicker(d.h, d.m, d.s);
        if (timeLeftSpan) timeLeftSpan.textContent = formatPlain(d.h, d.m);
      };
      tick();
      handle = setInterval(tick, 1000);
      return handle;
    }
    return null;
  }

  // ============================================================
  // Dialog — a single global Sephora-style modal, created lazily and reused
  // by every trigger (PDP + cart). Native <dialog> for focus-trap + a11y.
  // ============================================================

  const Dialog = {
    el: null,
    input: null,
    status: null,
    _pendingGeo: null,   // { postal, region, city } filled by "use current location"

    ensure: function () {
      if (this.el) return;
      const config = Store.config || {};

      const dialog = document.createElement('dialog');
      dialog.className = 'estimated-shipping-dialog';
      dialog.setAttribute('aria-label', config.dialogTitle || 'Shipping & Delivery Location');

      const form = document.createElement('form');
      form.method = 'dialog';
      form.className = 'estimated-shipping-dialog__form';

      // Header
      const header = document.createElement('div');
      header.className = 'estimated-shipping-dialog__header';
      const title = document.createElement('h2');
      title.className = 'estimated-shipping-dialog__title';
      title.textContent = config.dialogTitle || 'Shipping & Delivery Location';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'estimated-shipping-dialog__close';
      close.setAttribute('aria-label', config.cancelLabel || 'Close');
      close.textContent = '×';
      close.addEventListener('click', function () { Dialog.close(); });
      header.appendChild(title);
      header.appendChild(close);

      // Input
      const field = document.createElement('div');
      field.className = 'estimated-shipping-dialog__field';
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'estimated-shipping-dialog__input';
      input.setAttribute('autocomplete', 'postal-code');
      input.setAttribute('inputmode', 'text');
      input.placeholder = config.inputPlaceholder || 'Postal Code*';
      input.addEventListener('input', function () {
        Dialog._pendingGeo = null;
        Dialog._setStatus('');
      });
      field.appendChild(input);

      // Use current location
      const geo = document.createElement('button');
      geo.type = 'button';
      geo.className = 'estimated-shipping-dialog__geo';
      geo.textContent = config.useCurrentLocationLabel || 'Use current location';
      geo.addEventListener('click', function () { Dialog._useCurrentLocation(); });

      // Status / error line
      const status = document.createElement('p');
      status.className = 'estimated-shipping-dialog__status';
      status.setAttribute('role', 'status');
      status.hidden = true;

      // Footer
      const footer = document.createElement('div');
      footer.className = 'estimated-shipping-dialog__footer';
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'estimated-shipping-dialog__btn estimated-shipping-dialog__btn--cancel';
      cancel.textContent = config.cancelLabel || 'Cancel';
      cancel.addEventListener('click', function () { Dialog.close(); });
      const confirm = document.createElement('button');
      confirm.type = 'button';
      confirm.className = 'estimated-shipping-dialog__btn estimated-shipping-dialog__btn--confirm';
      confirm.textContent = config.confirmLabel || 'Confirm';
      confirm.addEventListener('click', function () { Dialog._confirm(); });
      footer.appendChild(cancel);
      footer.appendChild(confirm);

      form.appendChild(header);
      form.appendChild(field);
      form.appendChild(geo);
      form.appendChild(status);
      form.appendChild(footer);
      dialog.appendChild(form);

      // Backdrop click closes.
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) Dialog.close();
      });
      // Enter in the field submits.
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); Dialog._confirm(); }
      });

      document.body.appendChild(dialog);
      this.el = dialog;
      this.input = input;
      this.status = status;
    },

    open: function () {
      this.ensure();
      this._pendingGeo = null;
      this._setStatus('');
      const cur = Store.state && Store.state.postal ? Store.state.postal : '';
      this.input.value = cur;
      if (typeof this.el.showModal === 'function') this.el.showModal();
      else this.el.setAttribute('open', '');
      const self = this;
      setTimeout(function () { self.input.focus(); self.input.select(); }, 30);
    },

    close: function () {
      if (!this.el) return;
      if (typeof this.el.close === 'function' && this.el.open) this.el.close();
      else this.el.removeAttribute('open');
    },

    _setStatus: function (msg, isError) {
      if (!this.status) return;
      this.status.textContent = msg || '';
      this.status.hidden = !msg;
      this.status.classList.toggle('estimated-shipping-dialog__status--error', !!isError);
    },

    // The dialog only ever opens for the Canadian destination, so the country
    // is Canada — no guessing from the typed value.
    _country: function () {
      return (Store.state && Store.state.country) || (Store.config && Store.config.marketCountry) || 'CA';
    },

    _useCurrentLocation: async function () {
      const config = Store.config || {};
      this._setStatus(config.geoLocatingLabel || 'Detecting your location…');
      try {
        const pos = await getCurrentPosition();
        const res = await reverseGeocode(config.workerUrl, pos.lat, pos.lng);
        if (res && res.postal) {
          this._pendingGeo = { postal: res.postal, region: res.region, city: res.city };
          this.input.value = res.postal;
          this._setStatus((config.geoDetectedLabel || 'Detected:') + ' ' + res.postal + '. ' + (config.geoConfirmHint || 'Confirm to use it.'));
        } else {
          this._setStatus(config.geoUnavailableLabel || 'Could not determine your postal code. Please enter it manually.', true);
        }
      } catch (reason) {
        if (reason === 'denied') {
          this._setStatus(config.geoDeniedLabel || 'Location permission denied. Please allow it and try again, or enter your postal code manually.', true);
        } else if (reason === 'unsupported') {
          this._setStatus(config.geoUnsupportedLabel || 'Your browser does not support location. Please enter your postal code manually.', true);
        } else {
          this._setStatus(config.geoUnavailableLabel || 'Could not get your location. Please enter your postal code manually.', true);
        }
      }
    },

    _confirm: function () {
      const config = Store.config || {};
      const raw = this.input.value;
      const country = this._country();
      if (!validatePostalFormat(raw, country)) {
        this._setStatus(config.invalidLabel || 'Please enter a valid postal code.', true);
        return;
      }
      const normalized = normalizePostal(raw);
      const usePending = this._pendingGeo && normalizePostal(this._pendingGeo.postal) === normalized;
      const loc = usePending
        ? { postal: normalized, country: country, region: this._pendingGeo.region || null, city: this._pendingGeo.city || null, source: 'geolocation' }
        : { postal: normalized, country: country, region: null, city: null, source: 'manual' };
      Store.setLocation(loc);
      this.close();
    },
  };

  function openDialog() { Dialog.open(); }

  // ============================================================
  // Custom element — one per placement (PDP, cart). Reads config from its
  // data-attributes (identical everywhere, from global settings), subscribes
  // to the Store, and re-renders on every change.
  // ============================================================

  class EstimatedShipping extends HTMLElement {
    connectedCallback() {
      this._target = this.dataset.target || 'pdp';
      this._debug = this.hasAttribute('data-debug');
      this._unsub = null;
      this._tickerHandle = null;
      this._wantsVisible = false;
      this._countdownOnly = this.dataset.countdownOnly === 'true';

      // Parse this instance's config once; hand it to the Store (first wins).
      const config = readConfigFromDataset(this);

      this._watchVariantState();

      const url = new URL(window.location.href);
      const previewMode = url.searchParams.has('preview_theme_id');
      const opts = {
        debug: this._debug,
        debugScenario: this.dataset.debugScenario || null,
        testPostal: previewMode ? url.searchParams.get('test_postal') : null,
        testCountry: previewMode ? url.searchParams.get('test_country') : null,
        testRegion: previewMode ? url.searchParams.get('test_region') : null,
        now: previewMode ? parseTestNow(url.searchParams.get('test_now')) : null,
      };

      if (this._debug) this.removeAttribute('hidden');

      // Init the store (idempotent) and always subscribe for updates.
      Store.init(config, opts);
      this._unsub = Store.subscribe(this._onState.bind(this));
      if (Store.state) this._onState(Store.state);
    }

    disconnectedCallback() {
      if (this._tickerHandle) { clearInterval(this._tickerHandle); this._tickerHandle = null; }
      if (this._unsub) { this._unsub(); this._unsub = null; }
      if (this._variantObserver) { this._variantObserver.disconnect(); this._variantObserver = null; }
    }

    // ---- PDP variant gate ----
    // The PDP block stays hidden until a variant is selected (click or ?variant=
    // in the URL) and while the selected variant is sold out ("Notify me").
    // Both are read from the theme's own state classes: `variant-selected` on
    // <variant-selects id="variant-selects-{section}"> and `soldoutVariant` on
    // `.staticAddToCart .product-form__buttons`, which js--all-dawn.js toggles.
    _gateElements() {
      const sid = this.dataset.sectionId;
      if (this._target !== 'pdp' || !sid) return { picker: null, buttons: null };
      const root = this.closest('product-info') || document;
      return {
        // Absent on single-variant products → treated as selected.
        picker: document.getElementById('variant-selects-' + sid),
        buttons: root.querySelector('.staticAddToCart .product-form__buttons'),
      };
    }

    _gateOpen() {
      const els = this._gateElements();
      if (els.picker && !els.picker.classList.contains('variant-selected')) return false;
      if (els.buttons && els.buttons.classList.contains('soldoutVariant')) return false;
      return true;
    }

    _watchVariantState() {
      const els = this._gateElements();
      if (!els.picker && !els.buttons) return;
      const self = this;
      this._variantObserver = new MutationObserver(function () { self._applyVisibility(); });
      [els.picker, els.buttons].forEach(function (el) {
        if (el) self._variantObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
      });
    }

    // Show only when the render wants to AND the variant gate is open.
    _applyVisibility() {
      if (this._wantsVisible && this._gateOpen()) this.removeAttribute('hidden');
      else this.setAttribute('hidden', '');
    }

    _reveal() {
      this._wantsVisible = true;
      this._applyVisibility();
    }

    _onState(state) {
      if (this._tickerHandle) { clearInterval(this._tickerHandle); this._tickerHandle = null; }
      if (this._debug) this._renderDebug(state);
      this._render(state);
    }

    _render(state) {
      const config = Store.config || {};
      const target = this._target;
      const notifEl = this.querySelector('[data-notification]');
      const locEl = this.querySelector('[data-location]');
      if (!notifEl || !locEl) return;

      const marketCA = (config.marketCountry || '').toUpperCase() === 'CA';
      const hasPostal = !!(state && state.postal);

      this.classList.remove('estimated-shipping--empty');
      locEl.hidden = true;

      // Cart: apply the same-day / next-day cart toggles. Suppressed overrides
      // fall through to the standard regional message (never hidden), so with
      // both off the cart shows the plain Canadian estimate — same global layer
      // as US/UK/EU/International.
      let res = state && state.resolution;
      if (target === 'cart' && marketCA && res) {
        res = computeResolution(state, config, Store.now, {
          suppressSameDay: !config.cartShowSd,
          suppressNextDay: !config.cartShowNd,
        });
      }
      const template = this._pickTemplate(res, config);
      const hasMessage = !!template;

      // "Only show Obibox times": show nothing unless a same-day / next-day
      // countdown is live or it's the weekend Monday message. Standard,
      // international messages and the postal prompt are suppressed.
      if (this._countdownOnly) {
        if (!hasMessage || !isObiboxTime(res, Date.now())) { this._hide(notifEl); return; }
      }

      // ---- Non-Canada: static country message (no postal UI, no dialog). On
      // PDP always; in the cart once the free-shipping threshold is reached
      // (Liquid only renders the cart block then). ----
      if (!marketCA) {
        if (!hasMessage) { this._hide(notifEl); return; }
        this._renderNotif(notifEl, template, res, state);
        this._reveal();
        return;
      }

      // ---- Canada ----

      // CART: show whenever we have a postal + a message (no confirmation gate).
      // The toggles already picked the right zone above (SD / ND / standard).
      if (target === 'cart') {
        if (hasPostal && hasMessage) {
          this._renderNotif(notifEl, template, res, state);
          this._renderLocationRow(locEl, state, config);
          this._reveal();
        } else {
          this._hide(notifEl);
        }
        return;
      }

      // PDP: no postal yet → prompt to set it.
      if (!hasPostal) {
        notifEl.hidden = true;
        this._renderPrompt(locEl, config);
        this.classList.add('estimated-shipping--empty');
        this._reveal();
        return;
      }

      // PDP: postal known but nothing to advertise (not served today) → just
      // the location row so the visitor can still change it.
      if (!hasMessage) {
        notifEl.hidden = true;
        this._renderLocationRow(locEl, state, config);
        this._reveal();
        return;
      }

      // PDP: full render — notification + location row.
      this._renderNotif(notifEl, template, res, state);
      this._renderLocationRow(locEl, state, config);
      this._reveal();
    }

    // Pick the copy for this placement. Visibility is decided by the shared
    // resolution (res.template === '' → nothing to show); the cart just swaps
    // in its own per-zone wording when provided, else reuses the PDP text.
    _pickTemplate(res, config) {
      if (!res || !res.template) return '';
      if (this._target === 'cart') {
        const cart = config['cartTemplate' + toAttrCase(res.effectiveZone)];
        if (cart) return cart;
      }
      return res.template;
    }

    _hide(notifEl) {
      if (notifEl) notifEl.hidden = true;
      this._wantsVisible = false;
      this.setAttribute('hidden', '');
    }

    _renderNotif(notifEl, template, res, state) {
      const handle = renderNotification(notifEl, {
        template: template,
        dayWord: res.dayWord,
        postal: state.postal,
        targetDate: res.targetDate,
        onExpire: this._countdownOnly ? this._hide.bind(this, notifEl) : null,
      });
      if (handle) this._tickerHandle = handle;
      notifEl.hidden = false;
    }

    _renderLocationRow(locEl, state, config) {
      locEl.replaceChildren();
      locEl.hidden = false;

      const label = document.createElement('span');
      label.className = 'estimated-shipping__loc-label';
      label.appendChild(document.createTextNode((config.deliveryToLabel || 'Delivery to') + ' '));
      const strong = document.createElement('strong');
      strong.textContent = state.postal || '';
      label.appendChild(strong);
      locEl.appendChild(label);

      const changeBtn = document.createElement('button');
      changeBtn.type = 'button';
      changeBtn.className = 'estimated-shipping__change';
      changeBtn.textContent = config.changeLabel || 'Change';
      changeBtn.addEventListener('click', function () { openDialog(); });
      locEl.appendChild(changeBtn);
    }

    _renderPrompt(locEl, config) {
      locEl.replaceChildren();
      locEl.hidden = false;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'estimated-shipping__prompt';
      btn.textContent = config.promptLabel || 'Enter your postal code for a delivery estimate';
      btn.addEventListener('click', function () { openDialog(); });
      locEl.appendChild(btn);
    }

    _renderDebug(state) {
      const s = state || {};
      const res = s.resolution || {};
      setDebug(this, 'country', s.country);
      setDebug(this, 'region', s.region);
      setDebug(this, 'city', s.city);
      setDebug(this, 'postal', s.postal);
      setDebug(this, 'source', s.source);
      setDebug(this, 'fsa', s.row ? 'yes' : 'no');
      setDebug(this, 'zone', res.effectiveZone);
      const rowEl = this.querySelector('[data-debug-row]');
      if (rowEl) rowEl.textContent = s.row ? JSON.stringify(s.row, null, 2) : '—';
      setDebug(this, 'today', res.todayIdx != null ? DAY_NAMES[res.todayIdx] : '—');
      setDebug(this, 'now', res.nowLabel);
      setDebug(this, 'tz', res.timezone);
      setDebug(this, 'cutoffs', res.cutoffStr);
      setDebug(this, 'day', res.dayWord);
    }
  }

  // ============================================================
  // Config parsing (dataset → Store config object)
  // ============================================================

  function readConfigFromDataset(el) {
    const d = el.dataset;
    return {
      workerUrl: d.workerUrl || '',
      zonesUrl: d.zonesUrl || '',
      timezone: SHIP_FROM_TZ,
      marketCountry: (d.marketCountry || '').toUpperCase() || null,
      accountPostal: normalizePostal(d.accountPostal || ''),
      accountCountry: (d.accountCountry || '').toUpperCase() || null,
      accountRegion: (d.accountRegion || '').toUpperCase() || null,
      enableIp: d.enableIp !== 'false',
      // Cart placement toggles (Canada same-day / next-day).
      cartShowSd: d.cartShowSd === 'true',
      cartShowNd: d.cartShowNd !== 'false',
      todayWord: d.todayWord || 'today',
      tomorrowWord: d.tomorrowWord || 'tomorrow',
      mondayWord: d.mondayWord || 'Monday',
      // Weekend Monday (Obibox) copy: theme setting, falling back to the locale files.
      templateMonday: d.templateMonday, cartTemplateMonday: d.cartTemplateMonday,
      // Cut-offs exist only for Same-day / Next-day; standard zones are static.
      cutoffSd: d.cutoffSd, cutoffNd: d.cutoffNd,
      // Product-page templates per zone.
      templateSd: d.templateSd, templateNd: d.templateNd,
      templateCaQcOn: d.templateCaQcOn, templateCaOther: d.templateCaOther,
      templateUs: d.templateUs, templateUk: d.templateUk,
      templateEu: d.templateEu, templateIntl: d.templateIntl,
      // Cart-specific copy per zone (falls back to the PDP template when empty).
      cartTemplateSd: d.cartTemplateSd, cartTemplateNd: d.cartTemplateNd,
      cartTemplateCaQcOn: d.cartTemplateCaQcOn, cartTemplateCaOther: d.cartTemplateCaOther,
      cartTemplateUs: d.cartTemplateUs, cartTemplateUk: d.cartTemplateUk,
      cartTemplateEu: d.cartTemplateEu, cartTemplateIntl: d.cartTemplateIntl,
      // UI copy.
      deliveryToLabel: d.deliveryToLabel,
      changeLabel: d.changeLabel,
      confirmLabel: d.confirmLabel,
      cancelLabel: d.cancelLabel,
      promptLabel: d.promptLabel,
      dialogTitle: d.dialogTitle,
      inputPlaceholder: d.inputPlaceholder,
      useCurrentLocationLabel: d.useCurrentLocationLabel,
      geoLocatingLabel: d.geoLocatingLabel,
      geoDetectedLabel: d.geoDetectedLabel,
      geoConfirmHint: d.geoConfirmHint,
      geoDeniedLabel: d.geoDeniedLabel,
      geoUnavailableLabel: d.geoUnavailableLabel,
      geoUnsupportedLabel: d.geoUnsupportedLabel,
      invalidLabel: d.invalidLabel,
    };
  }

  // ============================================================
  // Misc browser helpers
  // ============================================================

  function parseTestNow(s) {
    if (!s) return null;
    const d = new Date(s);
    return isFinite(d.getTime()) ? d : null;
  }

  function setDebug(el, key, value) {
    const target = el.querySelector('[data-debug-' + key + ']');
    if (!target) return;
    target.textContent = value == null || value === '' ? '—' : String(value);
  }

  // ============================================================
  // Node export (tests)
  // ============================================================

  if (typeof window === 'undefined' && typeof module !== 'undefined') {
    module.exports = {
      normalizePostal: normalizePostal,
      parseCutoff: parseCutoff,
      nowMinutes: nowMinutes,
      findRow: findRow,
      resolveZone: resolveZone,
      toAttrCase: toAttrCase,
      nextCutoffDate: nextCutoffDate,
      formatDuration: formatDuration,
      formatTicker: formatTicker,
      isObiboxTime: isObiboxTime,
      inferCountryFromPostal: inferCountryFromPostal,
      caRegionFromPostal: caRegionFromPostal,
      validatePostalFormat: validatePostalFormat,
      computeResolution: computeResolution,
      readCache: readCache,
      writeCache: writeCache,
      clearCache: clearCache,
      CACHE_KEY: CACHE_KEY,
      CACHE_TTL_MS: CACHE_TTL_MS,
      EUROZONE_CODES: EUROZONE_CODES,
      CA_QC_ON: CA_QC_ON,
    };
    return;
  }

  customElements.define('estimated-shipping', EstimatedShipping);
})();
